import * as React from 'react';

import { TabBar } from '@longline/aqua-ui/controls/TabBar';

import { useUsersContext } from '../IUsersContext';
import { Pane } from '../../../ui/Pane';
import { TResourceState } from '../../../types/TResourceState';
import { IUser } from '../../../api/models';
import { ISystemFilter, SystemFilter } from "../../../filters/SystemFilter";
import { SystemSelection } from '../SystemSelection';
import { AddUserForm } from './AddUserForm';
import { Role } from '../../../api/Permission';

const AddUser = () => {
  const ctx = useUsersContext();

  // Cast via unknown because the new-user starter has no uuid/email/name
  // yet; the form fills these in before submit.
  const user = React.useRef<IUser>({systems: [], roles: [ Role.User ]} as unknown as IUser);
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [readyState, setReadyState] = React.useState<TResourceState>('ready');
  const [filter, setFilter] = React.useState<ISystemFilter>({});
  const [tab, setTab] = React.useState<'data'|'systems'>('data');
  const [dirty, setDirty] = React.useState(false);

  const controller = React.useRef<AbortController | null>(null);
  
  const handleChange = (value: IUser, forceUpdate: boolean) => {
    Object.assign(user.current, value);
  }

  const handleSubmit = async () => {
    if(readyState == 'loading') return;

    setTab('data');

    if(!isValid) {
      setDirty(true);
      return;
    }

    // Systems list must be a flat list of UUIDs:
    const u = {
      ...user.current,
      systems: user.current.systems.filter(s => (s as any).checked).map(s => s.uuid)
    }
    setReadyState('loading');
    controller.current = new AbortController();
    const result = await ctx.createUser(u as any, controller.current.signal);
    setReadyState(result);
  }

  React.useEffect(() => {
    return () => {
      if(controller.current) {
        controller.current.abort();
        console.warn("Add user aborted.");
      }
    }
  }, []);

  return (
    <>
      <Pane.Heading>
        {tab == 'data' && <Pane.Title>Users</Pane.Title>}
        {tab == 'systems' && <SystemFilter filter={filter} onChange={setFilter}/>}
      </Pane.Heading>
      <Pane.Subheader expandable>
        Add user
      </Pane.Subheader>
      
      <TabBar active={tab === 'systems' ? 1 : 0} line>
        <TabBar.Tab onClick={() => setTab('data')}>Data</TabBar.Tab>
        <TabBar.Tab onClick={() => setTab('systems')}>Systems</TabBar.Tab>
      </TabBar>

      <div style={{display: tab === 'data' ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column' }}>
        <Pane.Body>
          <AddUserForm dirty={dirty} user={user.current} onChange={handleChange} onValidate={(v: boolean) => setIsValid(v)}/>
        </Pane.Body>
      </div>
      
      <div style={{display: tab === 'systems' ? 'block' : 'none', flex: 1 }}>
        <SystemSelection user={user.current} onChange={handleChange} filter={filter} onClearFilter={() => setFilter({})}/>
      </div>     

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('list')}/>
        <Pane.SubmitButton onClick={() => handleSubmit()} working={readyState=='loading'} disabled={readyState == 'loading'}/>
      </Pane.Footer> 
    </>
  )
}

export { AddUser }
